import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MessageReactions from "./MessageReactions";
import MessageActions from "./MessageActions";
import MessageReadIndicators from "./MessageReadIndicators";
import SharedContentCard from "./SharedContentCard";
import MessageImages from "./MessageImages";
import ReplyPreview, { getReplyPreviewSource } from "./ReplyPreview";
import { defaultPfpUrl } from "../../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { cn } from "../../../lib/utils";
import { getMessageImageUrls } from "../../../lib/contentImages";
import {
  FormattedBody,
  FORMATTED_BODY_LINK_ON_ACCENT_CLASS,
  FORMATTED_BODY_MENTION_ON_ACCENT_CLASS,
} from "../../ui/formatted-body";

/**
 * Incoming sender avatar (or profile link wrapper).
 * @param {Object} props
 * @param {Object} props.message
 * @param {string|null} props.senderProfilePath
 * @returns {JSX.Element}
 */
function SenderAvatar({ message, senderProfilePath }) {
  const avatar = (
    <Avatar className="h-8 w-8 shrink-0">
      <AvatarImage
        src={message.sender_pfp || defaultPfpUrl}
        alt={message.sender_name || "Profile"}
        onError={(e) => {
          e.target.src = defaultPfpUrl;
        }}
      />
      <AvatarFallback>
        {(message.sender_name || "?").slice(0, 1).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );

  if (!senderProfilePath) {
    return avatar;
  }

  return (
    <Link
      to={senderProfilePath}
      className="shrink-0 rounded-full outline-none ring-offset-2 ring-offset-primary focus-visible:ring-2 focus-visible:ring-ui-ring"
      title={
        message.sender_name
          ? `View ${message.sender_name}'s profile`
          : "View profile"
      }
      aria-label={
        message.sender_name
          ? `View ${message.sender_name}'s profile`
          : "View sender profile"
      }
    >
      {avatar}
    </Link>
  );
}

/**
 * Message bubble row with inline hover actions (minimal icons).
 * Consecutive same-sender clusters: header on the first message, avatar on the last.
 * @param {Object} props
 * @param {string} props.chatType
 * @param {Object} props.message
 * @param {boolean} props.isOwnMessage
 * @param {boolean} props.animateOnMount
 * @param {number} props.userId
 * @param {number} props.chatId
 * @param {boolean} [props.showHeader=true] - Sender name above the bubble (group cluster start).
 * @param {boolean} [props.showAvatar=true] - Sender pfp beside the bubble (cluster end).
 * @param {string} [props.scrollAnchorId] - Id used to keep the viewport locked during pagination.
 * @param {(messageId: number) => void} [props.onMessageDeleted]
 * @param {(reactionId: number) => void|Promise<void>} [props.onRemoveOwnReaction]
 * @param {(message: Object) => void} [props.onReply]
 * @param {(parentId: number) => void} [props.onJumpToParent]
 * @param {boolean} [props.highlighted]
 * @param {Array<{ user_id: number, name?: string, pfp?: string }>} [props.readers] - Members whose last-read is this message
 * @returns {JSX.Element | null}
 */
function Message({
  chatType,
  message,
  isOwnMessage,
  animateOnMount,
  userId,
  chatId,
  showHeader = true,
  showAvatar = true,
  scrollAnchorId,
  onMessageDeleted,
  onRemoveOwnReaction,
  onReply,
  onJumpToParent,
  highlighted = false,
  readers = [],
}) {
  if (!message) return null;

  if (message.message_type === "system") {
    return (
      <motion.div
        initial={animateOnMount ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex w-full justify-center px-4 py-1"
      >
        <FormattedBody
          as="p"
          className="max-w-md text-center text-xs text-text-muted"
        >
          {message.content}
        </FormattedBody>
      </motion.div>
    );
  }

  const isShare =
    message.message_type === "post" || message.message_type === "event";
  const isDeleted = Boolean(message.deleted_at);
  const imageUrls = isDeleted ? [] : getMessageImageUrls(message);
  const hasImages = imageUrls.length > 0;
  const hasCaption = Boolean((message.content || "").trim());
  const isReply = Number(message.parent_id) > 0;
  const hasReactions =
    !isDeleted && Array.isArray(message.reactions) && message.reactions.length > 0;
  const senderProfilePath =
    message.sender_id !== undefined && message.sender_id !== null
      ? `/profile/${message.sender_id}`
      : null;
  const replySenderLabel = isOwnMessage
    ? "You"
    : message.sender_name || "Someone";
  const repliedToViewer =
    Number(message.parent_sender_id) === Number(userId) && Number(userId) > 0;
  const replyTargetLabel = repliedToViewer
    ? "You"
    : message.parent_sender_name || "a message";
  const showSenderName =
    !isReply &&
    !isOwnMessage &&
    showHeader &&
    chatType === "group" &&
    Boolean(message.sender_name);

  const wrapperClass = cn(
    "group/message relative flex w-full min-w-0 max-w-full",
    isOwnMessage ? "justify-end" : "justify-start"
  );
  const bubbleClass = cn(
    "max-w-xs rounded-2xl px-4 py-2 text-sm shadow-sm lg:max-w-md",
    isOwnMessage
      ? "bg-message-sent text-text-on-accent hover:bg-message-sent-hover"
      : "bg-message-received text-text-primary hover:bg-message-received-hover",
    isDeleted && "opacity-90"
  );

  return (
    <div className="w-full min-w-0">
      <motion.div
        initial={animateOnMount ? { opacity: 0, y: 12 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className={wrapperClass}
      >
        <div className={cn("relative", isOwnMessage ? "mr-2" : "ml-2")}>
          <div
            className={cn(
              "flex items-end gap-2",
              isOwnMessage ? "justify-end" : "justify-start"
            )}
          >
            {!isOwnMessage &&
              (showAvatar ? (
                <SenderAvatar
                  message={message}
                  senderProfilePath={senderProfilePath}
                />
              ) : (
                <div className="h-8 w-8 shrink-0" aria-hidden />
              ))}

            <div
              className={cn(
                "flex min-w-0 flex-col",
                isOwnMessage ? "items-end" : "items-start"
              )}
            >
              {isReply ? (
                <div
                  className={cn(
                    "mb-1 max-w-xs text-xs text-ui-muted-foreground lg:max-w-md",
                    isOwnMessage && "text-right"
                  )}
                >
                  {replySenderLabel} replied to {replyTargetLabel}
                </div>
              ) : showSenderName ? (
                <div className="mb-1 flex items-center gap-2 text-xs text-ui-muted-foreground">
                  {senderProfilePath ? (
                    <Link
                      to={senderProfilePath}
                      className="rounded-sm font-medium text-ui-muted-foreground hover:text-ui-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                    >
                      {message.sender_name}
                    </Link>
                  ) : (
                    <span className="font-medium">{message.sender_name}</span>
                  )}
                </div>
              ) : null}

              {isReply ? (
                <ReplyPreview
                  source={getReplyPreviewSource(message, {
                    fromParentFields: true,
                  })}
                  isOwnMessage={isOwnMessage}
                  onClick={() => onJumpToParent?.(Number(message.parent_id))}
                />
              ) : null}

              <div
                className={cn(
                  "relative inline-flex max-w-full",
                  isOwnMessage && "flex-row-reverse"
                )}
              >
                <div
                  className={cn(
                    "relative ring-2 transition-[box-shadow] duration-1000",
                    highlighted ? "ring-message-focus" : "ring-transparent",
                    hasImages && !isDeleted
                      ? cn(
                          "flex max-w-xs flex-col gap-1 rounded-2xl lg:max-w-md",
                          isOwnMessage ? "items-end" : "items-start"
                        )
                      : "rounded-2xl",
                    hasImages && hasReactions && "pb-3",
                    (!isShare || isDeleted) && !hasImages && bubbleClass
                  )}
                  data-message-anchor={scrollAnchorId || undefined}
                >
                  {isDeleted ? (
                    <p
                      className={cn(
                        "italic",
                        isOwnMessage
                          ? "text-text-on-accent/70"
                          : "text-text-muted"
                      )}
                    >
                      This message was deleted
                    </p>
                  ) : isShare ? (
                    <SharedContentCard
                      message={message}
                      isOwnMessage={isOwnMessage}
                    />
                  ) : (
                    <>
                      {hasImages ? <MessageImages urls={imageUrls} /> : null}
                      {hasCaption ? (
                        <FormattedBody
                          className={cn(
                            "text-inherit",
                            hasImages && bubbleClass
                          )}
                          mentions={message.mentions}
                          linkClassName={
                            isOwnMessage
                              ? FORMATTED_BODY_LINK_ON_ACCENT_CLASS
                              : undefined
                          }
                          mentionClassName={
                            isOwnMessage
                              ? FORMATTED_BODY_MENTION_ON_ACCENT_CLASS
                              : undefined
                          }
                        >
                          {message.content}
                        </FormattedBody>
                      ) : null}
                    </>
                  )}
                  {hasReactions ? (
                    <MessageReactions
                      reactions={message.reactions}
                      userId={userId}
                      isOwnMessage={isOwnMessage}
                      onRemoveOwnReaction={onRemoveOwnReaction}
                    />
                  ) : null}
                </div>
                {message.client_pending ? null : (
                  <MessageActions
                    message={message}
                    isOwnMessage={isOwnMessage}
                    userId={userId}
                    chatId={chatId}
                    onMessageDeleted={onMessageDeleted}
                    onReply={onReply}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <MessageReadIndicators readers={readers} />
    </div>
  );
}

export default Message;
